"use client";
import React, { ReactNode, useEffect, useRef } from "react";
import { cn } from "utils/utils";

type CheckboxSize = "xs" | "sm" | "md" | "lg" | "xl";
type CheckboxVariant =
  | "primary"
  | "secondary"
  | "accent"
  | "neutral"
  | "success"
  | "warning"
  | "info"
  | "error";

const SIZE_CHECKBOX_CLASSES: Record<CheckboxSize, string> = {
  xs: "checkbox-xs",
  sm: "checkbox-sm",
  md: "checkbox-md",
  lg: "checkbox-lg",
  xl: "checkbox-xl",
};

const SIZE_TEXT_CLASSES: Record<CheckboxSize, string> = {
  xs: "text-sm",
  sm: "text-sm",
  md: "text-sm",
  lg: "text-lg",
  xl: "text-xl",
};

const VARIANT_CHECKBOX_CLASSES: Record<CheckboxVariant, string> = {
  primary: "checkbox-primary",
  secondary: "checkbox-secondary",
  accent: "checkbox-accent",
  neutral: "checkbox-neutral",
  success: "checkbox-success",
  warning: "checkbox-warning",
  info: "checkbox-info",
  error: "checkbox-error",
};

const VARIANT_LABEL_CLASSES: Record<CheckboxVariant, string> = {
  primary: "border-primary/50 bg-primary/5",
  secondary: "border-secondary/50 bg-secondary/5",
  accent: "border-accent/50 bg-accent/5",
  neutral: "border-neutral/50 bg-neutral/5",
  success: "border-success/50 bg-success/5",
  warning: "border-warning/50 bg-warning/5",
  info: "border-info/50 bg-info/5",
  error: "border-error/50 bg-error/5",
};

const VARIANT_TEXT_CLASSES: Record<CheckboxVariant, string> = {
  primary: "text-primary",
  secondary: "text-secondary",
  accent: "text-accent",
  neutral: "text-neutral",
  success: "text-success",
  warning: "text-warning",
  info: "text-info",
  error: "text-error",
};

interface CheckboxProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onChange" | "size" | "checked" | "onClick"
  > {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  size?: CheckboxSize;
  variant?: CheckboxVariant;
  icon?: ReactNode;
  indeterminate?: boolean;
  onClick?: React.MouseEventHandler<HTMLLabelElement>;
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled = false,
  size = "sm",
  variant = "primary",
  className,
  icon,
  indeterminate = false,
  onClick,
  ...inputProps
}: CheckboxProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  const checkboxClasses = cn(
    "checkbox",
    SIZE_CHECKBOX_CLASSES[size],
    VARIANT_CHECKBOX_CLASSES[variant]
  );
  return (
    <label
      className={cn(
        "border-base-content/20 rounded-field flex cursor-pointer select-none items-center gap-2 border p-2 transition-all",
        checked && !disabled && VARIANT_LABEL_CLASSES[variant],
        disabled && "border-base-content/10 cursor-not-allowed opacity-50",
        className
      )}
      onClick={onClick}
    >
      {/* sr-only span prevents "empty label" a11y error when no visible label/icon is present.
          The label is implicitly associated with the input it wraps — no htmlFor needed. */}
      {!label && !icon && (
        <span className="sr-only">{inputProps["aria-label"]}</span>
      )}
      <input
        ref={inputRef}
        type="checkbox"
        className={checkboxClasses}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        {...inputProps}
      />
      {(icon || label) && (
        <span
          className={cn(
            "flex items-center gap-2 text-base",
            checked && !disabled && VARIANT_TEXT_CLASSES[variant],
            disabled && "text-base-content/50",
            SIZE_TEXT_CLASSES[size]
          )}
        >
          {icon}
          {label}
        </span>
      )}
    </label>
  );
}
